import {
  SearchAutocomplete,
  User,
  formatPhoneNumberForDisplay,
} from "@ews-admin/global-design-system";
import React from "react";
import { useTranslation } from "react-i18next";
import patientApi from "../../../services/patient.api";
import { PublicPatient } from "../../../types/patient.definition";
import { Order } from "../../../types/search.definition";

interface PatientSearchAutocompleteProps {
  onSelect: (patientId: string, patient?: PublicPatient) => void;
  selectedPatientId: string;
  disabled?: boolean;
}

export const PatientSearchAutocomplete: React.FC<
  PatientSearchAutocompleteProps
> = ({ onSelect, selectedPatientId, disabled = false }) => {
  const { t } = useTranslation();

  const searchPatients = async (keyword: string): Promise<PublicPatient[]> => {
    const response = await patientApi.searchPatientsPublic({
      paginationSettings: { limit: 20, offset: 0 },
      sortBy: { property: "id", order: Order.ASC },
      query: { keyword },
    });
    return response.data;
  };

  const getPatientPublic = async (id: string): Promise<PublicPatient> => {
    return patientApi.getPatientPublic(id);
  };

  const getPatientDisplayValue = (patient: PublicPatient) => {
    return `${patient.firstName ?? ""} ${patient.lastName ?? ""}`.trim();
  };

  const getPatientSecondaryText = (patient: PublicPatient) => {
    const parts: string[] = [];
    if (patient.email) parts.push(patient.email);
    if (patient.phoneNumber)
      parts.push(formatPhoneNumberForDisplay(patient.phoneNumber));
    return parts.join(" • ");
  };

  const handleSelect = (id: string, patient?: PublicPatient) => {
    onSelect(id, patient);
  };

  return (
    <SearchAutocomplete<PublicPatient>
      onSelect={handleSelect}
      selectedId={selectedPatientId}
      searchFunction={searchPatients}
      getEntityById={getPatientPublic}
      getDisplayValue={getPatientDisplayValue}
      getSecondaryText={getPatientSecondaryText}
      placeholder={t("appointment.manage.autocomplete.searchPatients")}
      noResultsText={t("appointment.manage.autocomplete.noResults")}
      icon={User}
      disabled={disabled}
      minSearchLength={2}
          getMinCharactersHint={(min) => t("common.searchMinCharacters", { count: min })}
      debounceTime={300}
    />
  );
};
